import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { DatePickerComponent } from '../../../viewer/components/space-detail-panel/sections/date-picker/date-picker';
import { PaperIconComponent, PaperButtonComponent } from '@ricoh-paper/angular';
import { TaskService } from '../../services/task.service';
import { TaskCapabilityService } from '../../../../core/services/task-capability.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ImFeatureFlagDirective } from '../../../../shared/directives/im-feature-flag.directive';
import { LocaleService } from '../../../../core/services/locale.service';
import {
  ScheduleTask,
  TaskPriority,
  FreeTimeGap,
  capabilityTaskSourceFor,
} from '../../models/schedule-types';

export type TaskDialogMode = 'create' | 'edit';

const PRIORITY_TO_GQL: Record<number, string> = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low' };

const PRIORITY_LABELS: Record<number, string> = {
  1: 'Urgent',
  2: 'High',
  3: 'Medium',
  4: 'Low',
};

/**
 * RESPONSIBILITY: Unified task dialog for creating and editing tasks.
 * Linear-style issue creator with Tiptap rich text editor.
 *
 * USED BY: ScheduleMyWeekComponent (modal overlay)
 */
@Component({
  selector: 'im-task-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, DatePickerComponent, PaperIconComponent, PaperButtonComponent, CdkOverlayOrigin, CdkConnectedOverlay,
    TranslocoPipe, ImFeatureFlagDirective,
  ],
  templateUrl: './task-dialog.component.html',
  styleUrl: './task-dialog.component.scss',
})
export class TaskDialogComponent implements OnInit, OnChanges {
  // ── Inputs ──────────────────────────────────────────

  readonly mode = input<TaskDialogMode>('create');
  readonly targetDate = input.required<string>();
  readonly task = input<ScheduleTask | null>(null);
  readonly initialType = input<'task' | 'focus'>('task');
  readonly gapStartMin = input<number | null>(null);
  readonly maxMinutes = input<number | null>(null);
  readonly availableGaps = input<FreeTimeGap[]>([]);
  /**
   * Presentation: 'dialog' (default) renders the centred modal card; 'docked'
   * renders a full-height right-edge panel — the Edit escalation from the
   * task view (`sp-task-detail`) uses this so the editor slides OVER the
   * read panel in the same position/width, reading as the panel flipping
   * into edit mode rather than a different, older surface.
   */
  readonly variant = input<'dialog' | 'docked'>('dialog');

  /** Host class hook for the docked variant's styles. */
  @HostBinding('class.td-host--docked')
  protected get dockedHost(): boolean {
    return this.variant() === 'docked';
  }
  /**
   * Busy time blocks on the target day — meetings, existing bookings,
   * other scheduled tasks. Drives the day-strip visualisation in the
   * Time row so the user can see WHAT'S OCCUPIED before picking a slot
   * instead of guessing into the void. Empty array = no data, hides
   * the strip. Coordinates are minutes-from-midnight (matches
   * FreeTimeGap shape).
   */
  readonly dayBusyBlocks = input<{ startMin: number; endMin: number; label?: string }[]>([]);

  // ── Outputs ─────────────────────────────────────────

  readonly saved = output<void>();
  readonly deleted = output<void>();
  readonly completed = output<void>();
  readonly dismissed = output<void>();
  /** Emits the new date string (YYYY-MM-DD) whenever the user changes
   *  the When property. Parent can listen to refresh the day-busy-blocks
   *  feed so the availability strip mirrors the chosen day. */
  readonly dateChanged = output<string>();

  // ── Services ────────────────────────────────────────

  private readonly taskService = inject(TaskService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly capabilities = inject(TaskCapabilityService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  // ── Editor ──────────────────────────────────────────

  readonly editorEl = viewChild<ElementRef<HTMLDivElement>>('editorEl');
  private editor: Editor | null = null;

  // ── Form state ──────────────────────────────────────

  protected readonly title = signal('');
  protected readonly priority = signal<TaskPriority>(3);
  protected readonly type = signal<'task' | 'focus'>('task');
  protected readonly duration = signal(30);
  protected readonly startMin = signal<number | null>(null);
  protected readonly isSaving = signal(false);
  protected readonly errorMsg = signal<string | null>(null);
  protected readonly showTimeInputs = signal(false);
  protected readonly priorityDropdownOpen = signal(false);
  protected readonly durationDropdownOpen = signal(false);
  protected readonly datePickerOpen = signal(false);
  protected readonly selectedDate = signal('');
  protected readonly syncToPlanner = signal(false);

  // ── AI helper ───────────────────────────────────────
  // "Help me write" — turn a one-line brief into a draft. Backend
  // calls Anthropic Haiku 4.5; failures resolve to null and we surface
  // a soft inline error so the user can retry or close the panel.
  protected readonly aiOpen = signal(false);
  protected readonly aiBrief = signal('');
  protected readonly aiBusy = signal(false);
  protected readonly aiError = signal<string | null>(null);

  protected readonly priorities: TaskPriority[] = [1, 2, 3, 4];
  protected readonly durationPresets = [
    { min: 15, label: '15m' },
    { min: 30, label: '30m' },
    { min: 45, label: '45m' },
    { min: 60, label: '1h' },
    { min: 90, label: '1h 30m' },
    { min: 120, label: '2h' },
  ];

  // ── Computed ────────────────────────────────────────

  protected readonly isEdit = computed(() => this.mode() === 'edit');
  protected readonly dialogTitle = computed(() => {
    this.locale.locale();
    return this.isEdit()
      ? this.transloco.translate('schedule.task_dialog.edit_task')
      : this.transloco.translate('schedule.task_dialog.new_task');
  });

  // ── Capability (for edit mode on external tasks) ──

  /** Capability source for this task — 'rdx' for native, 'planner'/'todo' for external. */
  protected readonly capabilitySource = computed(() => {
    const t = this.task();
    if (!t) return 'rdx' as const;
    return capabilityTaskSourceFor(t.source, t.sourceRef);
  });

  protected readonly isExternalTask = computed(() => this.capabilitySource() !== 'rdx');

  /** Can the user edit title/description/priority inline? */
  protected readonly canEditFields = computed(() => {
    if (!this.isEdit()) return true; // create mode — always editable for rdx source
    return this.capabilities.can(this.capabilitySource(), 'update-fields');
  });

  /** Can the user toggle completion? */
  protected readonly canCompleteTask = computed(() =>
    this.capabilities.can(this.capabilitySource(), 'update-status'),
  );

  /** Can the user delete the task? */
  protected readonly canDeleteTask = computed(() =>
    this.capabilities.can(this.capabilitySource(), 'delete'),
  );

  /**
   * When editing is restricted, this is the remediation string to show the user.
   * Falls back to a generic message if the restriction record doesn't have one.
   */
  protected readonly restrictionMessage = computed(() => {
    this.locale.locale();
    if (this.canEditFields()) return null;
    const r = this.capabilities.restrictionFor(this.capabilitySource(), 'update-fields');
    if (r?.remediation) return r.remediation;
    return this.transloco.translate('schedule.task_dialog.restriction_view_only');
  });

  /** Deep-link URL into the source system (e.g. planner.cloud.microsoft). */
  protected readonly deepLinkUrl = computed(() => {
    const t = this.task();
    if (t?.sourceUrl) return t.sourceUrl;
    const r =
      this.capabilities.restrictionFor(this.capabilitySource(), 'update-fields') ??
      this.capabilities.restrictionFor(this.capabilitySource(), 'update-status');
    return r?.deepLinkUrl ?? null;
  });

  /** Human-readable source label for buttons ("Open in Planner", etc.). */
  protected readonly sourceLabel = computed(() => {
    this.locale.locale();
    switch (this.capabilitySource()) {
      case 'planner': return 'Planner';
      case 'todo': return 'To Do';
      case 'jira': return 'Jira';
      case 'linear': return 'Linear';
      default: return this.transloco.translate('schedule.task_dialog.source_fallback');
    }
  });
  protected readonly hasTimeSlot = computed(() => this.startMin() !== null);
  protected readonly taskStatus = computed(() => this.task()?.status ?? 'planned');
  protected readonly isDone = computed(() => this.taskStatus() === 'done');

  protected readonly maxDuration = computed(() => {
    const max = this.maxMinutes();
    if (max && max > 0) return Math.min(Math.floor(max / 15) * 15, 240);
    return 240;
  });

  protected readonly endMin = computed(() => {
    const s = this.startMin();
    return s !== null ? s + this.duration() : null;
  });

  protected readonly startTimeStr = computed(() => {
    const s = this.startMin();
    return s !== null ? this.formatTime(s) : '09:00';
  });

  protected readonly endTimeStr = computed(() => {
    const e = this.endMin();
    return e !== null ? this.formatTime(e) : '09:30';
  });

  protected readonly currentPriority = computed(() => this.priorityLabel(this.priority()));

  protected readonly effectiveDate = computed(() => this.selectedDate() || this.targetDate());

  protected readonly dateLabel = computed(() => {
    this.locale.locale();
    const dateStr = this.effectiveDate();
    const d = new Date(dateStr + 'T12:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return this.transloco.translate('schedule.task_dialog.today');
    if (diff === 1) return this.transloco.translate('schedule.task_dialog.tomorrow');
    if (diff === -1) return this.transloco.translate('schedule.task_dialog.yesterday');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  });

  // ── Overlay positioning ─────────────────────────────
  // Positions for the CDK-portalled dropdowns (priority + date picker).
  // Anchor below the trigger by default and flip to above when there's
  // not enough room — keeps the panel inside the viewport. Both
  // dropdowns share this config since the trigger geometry is the
  // same shape (small chip).
  protected readonly dropdownPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  // ── Day-availability strip ──────────────────────────
  // The strip spans 7am–7pm (12 hours). Anything before/after gets
  // clipped — most workplace activity falls in that window and a wider
  // span makes individual blocks too narrow to read inside the dialog.
  protected readonly stripStartMin = 7 * 60;
  protected readonly stripEndMin = 19 * 60;
  protected readonly stripSpanMin = computed(() => this.stripEndMin - this.stripStartMin);

  /** Hour markers shown beneath the strip. Every 3 hours keeps the
   *  strip from feeling crowded at this width (~340px). */
  protected readonly stripHourMarks = computed(() => {
    const out: { label: string; pct: number }[] = [];
    for (let h = 7; h <= 19; h += 3) {
      const min = h * 60;
      const pct = ((min - this.stripStartMin) / this.stripSpanMin()) * 100;
      out.push({ label: `${h.toString().padStart(2, '0')}:00`, pct });
    }
    return out;
  });

  /** Busy blocks clipped to the visible strip range. */
  protected readonly stripBusyBlocks = computed(() => {
    const blocks = this.dayBusyBlocks();
    return blocks
      .map(b => ({
        startMin: Math.max(b.startMin, this.stripStartMin),
        endMin: Math.min(b.endMin, this.stripEndMin),
        label: b.label,
      }))
      .filter(b => b.endMin > b.startMin)
      .map(b => ({
        leftPct: ((b.startMin - this.stripStartMin) / this.stripSpanMin()) * 100,
        widthPct: ((b.endMin - b.startMin) / this.stripSpanMin()) * 100,
        label: b.label ?? null,
      }));
  });

  /** The user's currently-selected slot, as left/width percentages on the
   *  strip. Returns null when no time is set so the highlight hides. */
  protected readonly stripSelection = computed(() => {
    const start = this.startMin();
    if (start === null || !this.showTimeInputs()) return null;
    const end = start + this.duration();
    const visStart = Math.max(start, this.stripStartMin);
    const visEnd = Math.min(end, this.stripEndMin);
    if (visEnd <= visStart) return null;
    return {
      leftPct: ((visStart - this.stripStartMin) / this.stripSpanMin()) * 100,
      widthPct: ((visEnd - visStart) / this.stripSpanMin()) * 100,
    };
  });

  protected readonly suggestedSlots = computed(() => {
    const gaps = this.availableGaps();
    const dur = this.duration();
    return gaps
      .filter(g => g.durationMin >= dur)
      .map(g => ({
        startMin: g.startMin,
        endMin: g.startMin + dur,
        gapDuration: g.durationMin,
      }))
      .slice(0, 4);
  });

  // ── Lifecycle ───────────────────────────────────────

  ngOnInit(): void {
    requestAnimationFrame(() => this.initEditor());
    this.destroyRef.onDestroy(() => this.editor?.destroy());
  }

  ngOnChanges(): void {
    const t = this.task();
    if (this.isEdit() && t) {
      this.title.set(t.title);
      this.priority.set(t.priority);
      this.type.set(t.type === 'focus' ? 'focus' : 'task');
      this.duration.set(t.estimatedDuration || 30);
      if (t.scheduledStartTime) {
        this.startMin.set(this.timeStrToMin(t.scheduledStartTime));
        this.showTimeInputs.set(true);
      } else {
        this.startMin.set(null);
        this.showTimeInputs.set(false);
      }
      // Set editor content after init
      requestAnimationFrame(() => {
        if (this.editor && t.description) {
          this.editor.commands.setContent(t.description);
        }
      });
    } else {
      this.title.set('');
      this.priority.set(3);
      this.type.set(this.initialType());
      this.startMin.set(this.gapStartMin());
      this.showTimeInputs.set(this.gapStartMin() !== null);
      this.duration.set(this.pickDefaultDuration(this.maxMinutes()));
    }
    this.errorMsg.set(null);
  }

  // ── Editor ──────────────────────────────────────────

  private initEditor(): void {
    const el = this.editorEl()?.nativeElement;
    if (!el) return;

    // When editing an external task where update-fields is not available,
    // mount the editor read-only so the description is viewable but not mutable.
    const editable = !this.isEdit() || this.canEditFields();

    this.editor = new Editor({
      element: el,
      editable,
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
        }),
        Link.configure({ openOnClick: false }),
        Underline,
        Placeholder.configure({
          placeholder: editable ? this.transloco.translate('schedule.task_dialog.editor_placeholder') : '',
        }),
      ],
      editorProps: {
        attributes: { class: 'ProseMirror' },
      },
    });

    // Load existing content in edit mode
    const t = this.task();
    if (this.isEdit() && t?.description) {
      this.editor.commands.setContent(t.description);
    }
  }

  // ── Toolbar actions ─────────────────────────────────

  protected toggleBold(): void { this.editor?.chain().focus().toggleBold().run(); }
  protected toggleItalic(): void { this.editor?.chain().focus().toggleItalic().run(); }
  protected toggleUnderline(): void { this.editor?.chain().focus().toggleUnderline().run(); }
  protected toggleBulletList(): void { this.editor?.chain().focus().toggleBulletList().run(); }
  protected toggleOrderedList(): void { this.editor?.chain().focus().toggleOrderedList().run(); }
  protected toggleCodeBlock(): void { this.editor?.chain().focus().toggleCodeBlock().run(); }
  protected setHeading(level: 2 | 3): void { this.editor?.chain().focus().toggleHeading({ level }).run(); }

  protected isActive(name: string, attrs?: Record<string, unknown>): boolean {
    return this.editor?.isActive(name, attrs) ?? false;
  }

  // ── Helpers ─────────────────────────────────────────

  protected formatTime(min: number): string {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  protected durationLabel(min: number): string {
    if (min >= 60) {
      const h = Math.floor(min / 60);
      const m = min % 60;
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    }
    return `${min}m`;
  }

  protected priorityLabel(p: TaskPriority): string {
    return PRIORITY_LABELS[p] ?? `P${p}`;
  }

  private timeStrToMin(iso: string): number {
    if (iso.length <= 5) {
      const [h, m] = iso.split(':').map(Number);
      return h * 60 + m;
    }
    const d = new Date(iso);
    return d.getHours() * 60 + d.getMinutes();
  }

  private pickDefaultDuration(maxMin: number | null): number {
    if (!maxMin) return 30;
    const presets = [120, 60, 30, 15];
    for (const p of presets) {
      if (p <= maxMin) return p;
    }
    return 15;
  }

  private buildTimeIso(date: string, min: number): string {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${date}T${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;
  }

  // ── Time handlers ───────────────────────────────────

  protected enableTimeInputs(): void {
    this.startMin.set(540);
    this.showTimeInputs.set(true);
  }

  /**
   * Click anywhere on the day-strip to set the start time to that point.
   * Snaps to the nearest 15 minutes so the result is always sensible.
   * Duration is preserved — user can drag the slider afterwards.
   */
  protected onStripClick(event: MouseEvent): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const raw = this.stripStartMin + ratio * this.stripSpanMin();
    const snapped = Math.round(raw / 15) * 15;
    this.startMin.set(Math.max(this.stripStartMin, Math.min(this.stripEndMin - 15, snapped)));
    this.showTimeInputs.set(true);
  }

  protected onStartTimeChange(val: string): void {
    const [h, m] = val.split(':').map(Number);
    const newStart = h * 60 + m;
    const end = this.endMin();
    if (end !== null && newStart < end) {
      this.startMin.set(newStart);
      this.duration.set(end - newStart);
    }
  }

  protected onEndTimeChange(val: string): void {
    const [h, m] = val.split(':').map(Number);
    const newEnd = h * 60 + m;
    const start = this.startMin();
    if (start !== null && newEnd > start) {
      this.duration.set(newEnd - start);
    }
  }

  protected selectSuggestedSlot(slot: { startMin: number; endMin: number }): void {
    this.startMin.set(slot.startMin);
    this.duration.set(slot.endMin - slot.startMin);
    this.showTimeInputs.set(true);
  }

  protected selectPriority(p: TaskPriority): void {
    this.priority.set(p);
    this.priorityDropdownOpen.set(false);
  }

  protected selectDuration(min: number): void {
    this.duration.set(min);
    this.durationDropdownOpen.set(false);
  }

  protected onDateChange(dateStr: string): void {
    if (dateStr) {
      this.selectedDate.set(dateStr);
      this.datePickerOpen.set(false);
      this.dateChanged.emit(dateStr);
    }
  }

  /**
   * Quick-date helpers anchored to "now" so the user doesn't have to
   * navigate the calendar for the most common moves. `next-monday`
   * means *the first Monday strictly after today* — when today is
   * already a Monday it skips to the following week, which matches
   * how most calendars handle "schedule for next Monday".
   */
  protected onQuickDate(kind: 'today' | 'tomorrow' | 'next-monday'): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let target = new Date(today);
    if (kind === 'tomorrow') {
      target.setDate(target.getDate() + 1);
    } else if (kind === 'next-monday') {
      const day = target.getDay(); // 0=Sun, 1=Mon, …, 6=Sat
      const offset = ((1 - day + 7) % 7) || 7; // strictly future Monday
      target.setDate(target.getDate() + offset);
    }
    const yyyy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, '0');
    const dd = String(target.getDate()).padStart(2, '0');
    this.onDateChange(`${yyyy}-${mm}-${dd}`);
  }

  // Outside-click handling moved to (overlayOutsideClick) on each
  // CDK overlay — it correctly excludes clicks inside the portalled
  // panel even though that panel lives outside this component's DOM
  // tree. The previous document:click HostListener fired on the same
  // tick as the chip's click that opened the panel and slammed it
  // shut again.

  // ── Actions ─────────────────────────────────────────

  protected async save(): Promise<void> {
    const titleVal = this.title().trim();
    if (!titleVal || this.isSaving()) return;

    this.isSaving.set(true);
    this.errorMsg.set(null);

    try {
      const start = this.startMin();
      const dur = this.duration();
      const date = this.effectiveDate();
      const description = this.editor?.getHTML() || '';

      if (start !== null && start + dur > 1440) {
        this.errorMsg.set(this.transloco.translate('schedule.task_dialog.error_past_midnight'));
        this.isSaving.set(false);
        return;
      }

      const scheduledStartTime = start !== null ? this.buildTimeIso(date, start) : undefined;
      const scheduledEndTime = start !== null ? this.buildTimeIso(date, start + dur) : undefined;

      const t = this.task();
      if (this.isEdit() && t) {
        const updates: Record<string, unknown> = {};
        if (titleVal !== t.title) updates['title'] = titleVal;
        if (description !== (t.description || '')) updates['description'] = description;
        if (this.priority() !== t.priority) updates['priority'] = PRIORITY_TO_GQL[this.priority()] || 'Medium';

        if (Object.keys(updates).length > 0) {
          await this.taskService.updateTask(t._id, updates);
        }

        const oldStart = t.scheduledStartTime ? this.timeStrToMin(t.scheduledStartTime) : null;
        if (start !== oldStart || (scheduledEndTime && scheduledEndTime !== t.scheduledEndTime)) {
          if (scheduledStartTime && scheduledEndTime) {
            await this.taskService.scheduleTask(t._id, date, scheduledStartTime, scheduledEndTime);
          }
        }

        this.saved.emit();
      } else {
        const task = await this.taskService.createTask({
          title: titleVal,
          description,
          priority: this.priority(),
          type: this.type(),
          scheduledDate: date,
          estimatedDuration: dur,
          scheduledStartTime,
          scheduledEndTime,
          syncToPlanner: this.syncToPlanner(),
        });
        if (task) {
          this.saved.emit();
        } else {
          this.errorMsg.set(this.taskService.error()?.message || this.transloco.translate('schedule.task_dialog.error_failed_to_create'));
        }
      }
    } catch {
      this.errorMsg.set(this.transloco.translate('schedule.task_dialog.error_generic'));
    } finally {
      this.isSaving.set(false);
    }
  }

  protected onComplete(): void { this.completed.emit(); }
  protected onDelete(): void { this.deleted.emit(); }
  protected dismiss(): void { this.dismissed.emit(); }

  // ── AI helper actions ────────────────────────────────

  /** Cmd/Ctrl + Enter inside the brief textarea triggers Generate.
   *  Plain Enter inserts a newline (default textarea behaviour). */
  protected onAiBriefEnter(event: Event): void {
    const ev = event as KeyboardEvent;
    if (ev.metaKey || ev.ctrlKey) {
      ev.preventDefault();
      this.onGenerate();
    }
  }

  /**
   * Generate from the brief — fills title + description + suggested
   * priority/duration/type. Title and description always overwrite (the
   * user explicitly asked for a draft); the brief stays in the textarea
   * so they can iterate by tweaking the brief and clicking Generate again.
   */
  protected async onGenerate(): Promise<void> {
    const brief = this.aiBrief().trim();
    if (!brief || this.aiBusy()) return;

    this.aiBusy.set(true);
    this.aiError.set(null);

    try {
      const draft = await this.taskService.generateTaskFromBrief(brief);
      if (!draft) {
        this.aiError.set(this.transloco.translate('schedule.task_dialog.error_ai_draft'));
        return;
      }

      this.title.set(draft.title);
      if (this.editor && draft.description) {
        this.editor.commands.setContent(draft.description);
      }
      const priority = Math.min(4, Math.max(1, draft.priority)) as TaskPriority;
      this.priority.set(priority);
      const duration = Math.min(240, Math.max(15, Math.round(draft.estimatedMinutes / 15) * 15));
      this.duration.set(duration);
      this.type.set(draft.type === 'focus' ? 'focus' : 'task');

      // Collapse the panel — user can re-open if they want to iterate.
      this.aiOpen.set(false);
    } catch (err: unknown) {
      this.aiError.set((err instanceof Error ? err.message : null) ?? this.transloco.translate('schedule.task_dialog.error_generic'));
    } finally {
      this.aiBusy.set(false);
    }
  }

  /** Open the task in its source system. No-op if no deep-link is available. */
  protected openInSource(): void {
    const url = this.deepLinkUrl();
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
